<script setup lang="ts">
import { computed, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { UiText, UiIcon } from "@shopexperts/shared";
import type { AdminLeadQueueRow } from "@shopexperts/legacy-api";
import AdminLeadProjectLine from "./AdminLeadProjectLine.vue";
import {
  getStatusConfig,
  formatShortDate,
  getEarliestDate,
} from "@/v2Projects/utils/display";

const props = defineProps<{
  rows: AdminLeadQueueRow[];
}>();

const emit = defineEmits<{
  assign: [row: AdminLeadQueueRow];
  edit: [row: AdminLeadQueueRow];
  delete: [row: AdminLeadQueueRow];
  markResolved: [row: AdminLeadQueueRow];
}>();

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
const showAdditional = ref(true);

const leadRow = computed(() => props.rows[0] ?? null);
const matchedRows = computed(() => props.rows.filter((row) => row.project));

const leadStatus = computed(() => {
  if (matchedRows.value.length === 0) {
    return (leadRow.value?.lead.status ?? "pending") as string;
  }

  const statuses = matchedRows.value
    .map((row) => row.project?.project_status)
    .filter((value): value is string => Boolean(value));

  if (statuses.includes("in_progress")) return "in_progress";
  if (statuses.includes("submitted")) return "submitted";
  if (statuses.includes("quoted")) return "quoted";
  if (statuses.includes("completed")) return "completed";
  if (statuses.includes("pending")) return "pending";
  return statuses[0] ?? "pending";
});

const leadStatusProjectType = computed(() => {
  if (leadStatus.value !== "pending") return null;
  const pendingProjects = matchedRows.value
    .map((row) => row.project)
    .filter((project): project is NonNullable<typeof project> => Boolean(project) && project.project_status === "pending");

  if (!pendingProjects.length) return null;
  return pendingProjects.every((project) => project.project_type === "booking_call")
    ? "booking_call"
    : "standard";
});

const statusDisplay = computed(() => {
  if (matchedRows.value.length === 0) {
    if (leadRow.value?.lead.status === "pending") {
      return {
        label: "Awaiting Match",
        badgeClass: "bg-yellow-100 text-yellow-800",
      };
    }

    return {
      label: "Matched",
      badgeClass: "bg-green-100 text-green-800",
    };
  }

  const config = getStatusConfig(leadStatus.value as any, leadStatusProjectType.value);
  return {
    label: config.label,
    badgeClass: config.badgeClass,
  };
});

const createdDate = computed(() => {
  const dates = props.rows
    .map((row) => row.project?.created_at ?? null)
    .filter((value): value is string => Boolean(value));

  if (leadRow.value?.lead.created_at) {
    dates.unshift(leadRow.value.lead.created_at);
  }

  return formatShortDate(getEarliestDate(dates));
});

const selectedRow = computed(() => matchedRows.value[0] ?? null);

const additionalRows = computed(() => {
  if (!selectedRow.value) return [];
  return matchedRows.value.filter((row) => row !== selectedRow.value);
});

const additionalCount = computed(() => additionalRows.value.length);

const metadataItems = computed(() => {
  if (!leadRow.value) return [];

  const items = [
    { label: "Client", value: leadRow.value.client.name || `Client #${leadRow.value.client.id}` },
    { label: "Email", value: leadRow.value.client.owner_email || "No owner email" },
    { label: "Store", value: leadRow.value.store.title || "Untitled Store" },
    { label: "Budget", value: leadRow.value.lead.budget_range || "—" },
    { label: "Strategy", value: leadRow.value.lead.matching_strategy || "—" },
  ];

  if (leadRow.value.selected_expert?.name) {
    items.push({ label: "Selected Expert", value: leadRow.value.selected_expert.name });
  }

  return items;
});

const matchedExpertsLine = computed(() => {
  const names = props.rows
    .map((row) => row.matched_expert?.name)
    .filter((value, index, array): value is string => Boolean(value) && array.indexOf(value) === index);

  return names;
});

const leadBudget = computed(() => leadRow.value?.lead.budget_range ?? null);
const leadUrgency = computed(() => {
  const urgency = leadRow.value?.lead.urgency;
  return urgency ? LEAD_URGENCY_LABELS[urgency] ?? urgency : null;
});
const leadBrief = computed(() => leadRow.value?.lead.project_brief ?? null);

const menuItems = computed(() => {
  const row = leadRow.value;
  if (!row) return [];

  return [
    {
      key: "assign" as const,
      label: "Assign Experts",
      icon: "user-circle",
      hidden: !row.admin_actions.can_match,
    },
    {
      key: "edit" as const,
      label: "Edit Lead",
      icon: "pencil",
      hidden: false,
    },
    {
      key: "mark-resolved" as const,
      label: "Mark Lead Resolved",
      icon: "check-circle",
      hidden: row.lead.status !== "pending",
      disabled: matchedRows.value.length === 0,
    },
    {
      key: "delete" as const,
      label: "Delete Lead",
      icon: "delete",
      variant: "danger" as const,
      hidden: false,
    },
  ].filter((item) => !item.hidden);
});

function toggleMenu(event: MouseEvent) {
  event.stopPropagation();
  menuOpen.value = !menuOpen.value;
}

function handleAction(action: "assign" | "edit" | "mark-resolved" | "delete") {
  if (!leadRow.value) return;
  menuOpen.value = false;

  if (action === "assign") emit("assign", leadRow.value);
  if (action === "edit") emit("edit", leadRow.value);
  if (action === "mark-resolved") emit("markResolved", leadRow.value);
  if (action === "delete") emit("delete", leadRow.value);
}

onClickOutside(menuRef, () => {
  menuOpen.value = false;
});

const LEAD_URGENCY_LABELS: Record<string, string> = {
  urgent: "Urgent",
  standard: "Standard",
  as_soon_as_possible: "As soon as possible",
  within_the_next_week: "Within the next week",
  within_the_next_month: "Within the next month",
  open_to_expert_opinion: "Open to expert opinion",
};
</script>

<template>
  <article class="lead-card">
    <div class="flex items-center justify-between gap-3 mb-3">
      <span
        :class="statusDisplay.badgeClass"
        class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide"
      >
        {{ statusDisplay.label }}
      </span>

      <div class="flex items-center gap-2">
        <UiText
          v-if="createdDate"
          as="span"
          variant="p-small"
          class="text-gray-600 shrink-0"
        >
          Submitted {{ createdDate }}
        </UiText>

        <div ref="menuRef" class="relative" @click.stop>
          <button
            class="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
            :class="{ 'bg-gray-100 text-gray-700!': menuOpen }"
            @click="toggleMenu"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="10" cy="4" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="10" cy="16" r="1.5" />
            </svg>
          </button>

          <Transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="menuOpen"
              class="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg border border-gray-200 shadow-lg z-50 py-1 origin-top-right"
            >
              <button
                v-for="item in menuItems"
                :key="item.key"
                class="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors"
                :class="item.variant === 'danger'
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-gray-700 hover:bg-gray-50'"
                :disabled="item.disabled"
                @click="handleAction(item.key)"
              >
                <UiIcon :name="(item.icon as any)" class="w-4 h-4 shrink-0 opacity-60" />
                <UiText as="span" variant="p-small" :class="item.disabled ? 'opacity-50' : ''">
                  {{ item.label }}
                </UiText>
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </div>

    <UiText as="h4" variant="h5" class="font-semibold text-gray-900 leading-snug mb-2">
      {{ leadRow?.lead.project_title ?? "Untitled Project" }}
    </UiText>

    <div v-if="metadataItems.length > 0" class="flex items-center gap-2 flex-wrap mb-2">
      <UiText
        v-for="item in metadataItems"
        :key="item.label"
        as="span"
        variant="p-small"
        class="text-gray-600"
      >
        <span class="font-medium text-gray-900">{{ item.label }}:</span> {{ item.value }}
      </UiText>
    </div>

    <UiText v-if="matchedExpertsLine.length > 0" as="p" variant="p-small" class="text-gray-600 mb-2">
      <span class="font-medium text-gray-900">Matched Experts:</span>
      {{ matchedExpertsLine.join(", ") }}
    </UiText>

    <div class="projects-area">
      <template v-if="selectedRow">
        <AdminLeadProjectLine :row="selectedRow" />

        <div v-if="additionalCount > 0">
          <button
            type="button"
            class="additional-toggle"
            @click.stop="showAdditional = !showAdditional"
          >
            <span class="additional-toggle__line" />
            <UiText as="span" variant="p-small" class="additional-toggle__label">
              {{ additionalCount }} additional expert{{ additionalCount > 1 ? "s" : "" }}
              <UiIcon
                name="chevron-down"
                class="w-3 h-3 ml-0.5 inline-block transition-transform duration-200"
                :class="{ 'rotate-180': showAdditional }"
              />
            </UiText>
            <span class="additional-toggle__line" />
          </button>

          <template v-if="showAdditional">
            <template v-for="(row, idx) in additionalRows" :key="row.project?.id ?? `${row.lead.id}-${idx}`">
              <div v-if="idx > 0" class="line-divider" />
              <AdminLeadProjectLine :row="row" compact />
            </template>
          </template>
        </div>
      </template>

      <template v-else-if="matchedRows.length > 0">
        <template v-for="(row, idx) in matchedRows" :key="row.project?.id ?? `${row.lead.id}-${idx}`">
          <div v-if="idx > 0" class="line-divider" />
          <AdminLeadProjectLine :row="row" />
        </template>
      </template>

      <div v-else class="matching-state">
        <div v-if="leadBudget || leadUrgency" class="flex items-center gap-2 flex-wrap mb-2">
          <UiText v-if="leadBudget" as="span" variant="p-small" class="inline-flex items-center gap-1 text-gray-700">
            <UiIcon name="dollar-circle" class="w-3.5 h-3.5 text-gray-500" />
            {{ leadBudget }}
          </UiText>
          <template v-if="leadBudget && leadUrgency">
            <span class="text-gray-400">&middot;</span>
          </template>
          <UiText v-if="leadUrgency" as="span" variant="p-small" class="text-gray-700">
            {{ leadUrgency }}
          </UiText>
        </div>
        <UiText
          v-if="leadBrief"
          as="p"
          variant="p-small"
          class="text-gray-600 mb-3"
          :truncate="1"
          :show-more-button="true"
        >
          {{ leadBrief }}
        </UiText>
        <div class="flex items-center gap-2">
            <UiText as="span" variant="p-small" class="text-se-gray-500">
            Not Matched Yet
          </UiText>
        </div>
      </div>
    </div>
  </article>
</template>

<style scoped>
.lead-card {
  background: white;
  border: 1px solid var(--color-gray-200, #e5e7eb);
  border-radius: 0.75rem;
  padding: 1.25rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.lead-card:hover {
  border-color: var(--color-gray-300, #d1d5db);
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.04);
}

.projects-area {
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  border-top: 1px solid var(--color-gray-100, #f3f4f6);
  margin-top: 0.75rem;
}

.line-divider {
  height: 1px;
  background: var(--color-gray-100, #f3f4f6);
  margin: 0 1.25rem;
}

.additional-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 1.25rem;
  cursor: pointer;
  background: none;
  border: none;
}

.additional-toggle__line {
  flex: 1;
  height: 1px;
  background: var(--color-gray-200, #e5e7eb);
}

.additional-toggle__label {
  display: inline-flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-gray-500, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.additional-toggle:hover .additional-toggle__label {
  color: var(--color-gray-700, #374151);
}

.matching-state {
  padding: 0.75rem 1.25rem;
}

.matching-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-se-green, #22c55e);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}
</style>
